import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Step {
  id: string;
  label: string;
}

interface StepperProps extends React.ComponentPropsWithoutRef<'ol'> {
  steps: Step[];
  activeStep: number;
  /** Reads out as "Step 2 of 3"; colour alone leaves progress invisible. */
  progressLabel?: (current: number, total: number) => string;
  /**
   * Marks every step done, including the last. `index < activeStep` can never
   * complete the final step, so without this a finished flow still shows its
   * last step as in-progress.
   */
  allComplete?: boolean;
}

const Stepper = React.forwardRef<HTMLOListElement, StepperProps>(
  (
    {
      steps,
      activeStep,
      allComplete = false,
      progressLabel = (current, total) => `Step ${current} of ${total}`,
      className,
      ...props
    },
    ref,
  ) => (
    <ol
      ref={ref}
      aria-label={progressLabel(Math.min(activeStep + 1, steps.length), steps.length)}
      className={cn('flex items-start', className)}
      {...props}
    >
      {steps.map((step, index) => {
        const complete = allComplete || index < activeStep;
        const active = !allComplete && index === activeStep;

        return (
          <React.Fragment key={step.id}>
            <li
              aria-current={active ? 'step' : undefined}
              className="flex min-w-0 flex-col items-center gap-2 px-1"
            >
              <span
                data-state={complete ? 'complete' : active ? 'active' : 'upcoming'}
                className={cn(
                  'grid size-8 shrink-0 place-items-center rounded-full text-sm font-medium revy-figure',
                  'transition-colors duration-150 ease-shell',
                  complete && 'bg-primary text-primary-foreground',
                  active && 'bg-primary/15 text-primary ring-2 ring-primary',
                  !complete && !active && 'bg-muted text-muted-foreground',
                )}
              >
                {complete ? (
                  <Check data-testid="step-complete" className="size-4" strokeWidth={2.4} />
                ) : (
                  index + 1
                )}
              </span>
              <span
                className={cn(
                  'text-center text-xs leading-snug text-pretty',
                  active ? 'font-medium text-foreground' : 'text-muted-foreground',
                )}
              >
                {step.label}
              </span>
            </li>

            {index < steps.length - 1 && (
              <li aria-hidden="true" className="mt-4 min-w-6 flex-1">
                <span
                  data-testid="step-connector"
                  data-travelled={index < activeStep ? 'true' : 'false'}
                  className={cn(
                    'block h-px w-full transition-colors duration-150 ease-shell',
                    index < activeStep ? 'bg-primary' : 'bg-border',
                  )}
                />
              </li>
            )}
          </React.Fragment>
        );
      })}
    </ol>
  ),
);
Stepper.displayName = 'Stepper';

export { Stepper };
